import Validator from 'validatorjs/src/validator';
import Attributes from 'validatorjs/src/attributes';
import { isURL, isCurrency } from 'validator';

import {
  lang,
  CLIENT_AUDIO_TYPES,
  EASTER_EGG_BUDGET,
  MIN_BUDGET,
  MAX_BUDGET,
} from 'config';

Validator.register('url', url => {
  return isURL(url, { require_protocol: true, protocols: ['http', 'https'] });
}, lang('invalid-url-format', 'The url format is invalid.'));

Validator.register('valid-dollar-amount', totalBudget => {
  return isCurrency(totalBudget);
}, 'Your input is invalid. Please enter a valid dollar amount.');

Validator.register('total-budget-range', totalBudget => {
  const strippedBudget = totalBudget.replace(',', '');
  const dollarAmount = parseFloat(strippedBudget);
  const budgetWithinRange = dollarAmount >= MIN_BUDGET && dollarAmount <= MAX_BUDGET;

  return budgetWithinRange || totalBudget === EASTER_EGG_BUDGET;
}, `Please enter an amount between $${MIN_BUDGET}-$${MAX_BUDGET}.`);

Validator.register('any-true-values', obj => {
  return Object.keys(obj).some(k => obj[k] === true);
}, lang('at-least-one-platform', 'Please select at least one platform.'));

Validator.register('file-problem', (fileProperties) => {
  const { fail = true } = fileProperties;
  return !fail;
}, lang('file-problem', 'Looks like something went wrong. Please try again.'));

Validator.register('filesize-max', (fileProperties, requirement) => {
  const maxSize = parseInt(requirement, 10);
  const fileSize = parseInt(fileProperties.size, 10);

  return fileSize < maxSize;
}, lang('file-size-over-limit', 'Your image is too large for our upload service. Please upload an image less than :maxSize.'));

Attributes.replacements['filesize-max'] = (template, rule) => {
  let [maxSize] = rule.getParameters();
  maxSize = Math.ceil(parseInt(maxSize, 10) / 1024 / 1024);
  return template.replace(/:maxSize/, `${maxSize}MB`);
};

// Filetype's error message will be difficult to translate, as
// images have only two supported types but audio has three.
// Let's readdress this when we start translating, since we have
// the supported filetypes listed in the <label> already.
Validator.register('filetype', (fileProperties, requirement) => {
  const { type } = fileProperties;
  const acceptedFormats = requirement.split(',');

  return acceptedFormats.indexOf(type) > -1;
}, lang('unacceptable-file-extension', 'Invalid file extension. Please upload a :file1 or :file2 file.'));

Attributes.replacements.filetype = (template, rule) => {
  const audioFiles = ['.OGG, .MP3,', '.WAV'];
  const imageFiles = ['.JPG', '.PNG'];
  let files;
  const [testType] = rule.getParameters();
  if (CLIENT_AUDIO_TYPES.indexOf(testType) > -1) {
    files = audioFiles;
  } else {
    files = imageFiles;
  }
  return template.replace(/:file1/, files[0]).replace(/:file2/, files[1]);
};

Validator.register('image-dimensions', (fileProperties, requirement) => {
  return (fileProperties.imageDimensions === requirement);
}, lang('image-wrong-dimensions', 'Your image is :imageDimensions pixels. Please upload a :acceptedDimensions image.'));

Attributes.replacements['image-dimensions'] = (template, rule) => {
  const [acceptedDimensions] = rule.getParameters();
  const { inputValue: { imageDimensions } } = rule;
  return template.replace(/:acceptedDimensions/, acceptedDimensions).replace(/:imageDimensions/, imageDimensions);
};

Validator.register('audio-duration', (fileProperties, requirement) => {
  return (fileProperties.audioDuration <= requirement);
}, 'Your uploaded audio is too long. Only 15 or 30 second files are accepted.');

Validator.register('account-setup-country', (countrySelection) => {
  return countrySelection === 'United States';
}, 'We are only accepting US advertisers at this time.');

Validator.register('noop', () => true);

// Registers dummy callbacks since these rules are processed pre-validation and not within the normal validatorjs flow
Validator.register('feature-flag', () => true);
Validator.register('exclude-feature-flag', () => true);
Validator.register('exclude-criteria-for-feature-flag', () => true);

// Everyone within this feature flag should NOT have this validation rule applied
const excludeFeatureFlagPattern = /^exclude-feature-flag:([^|]*)/;
// Everyone within this feature flag should have the validation rule applied
const featureFlagPattern = /^feature-flag:([^|]*)/;
// The following rule criteria should not be applied for members within this feature flag
const excludeCriteriaForFeatureFlagPattern = /^exclude-criteria-for-feature-flag:([^|]*)/;

export default class AdValidator {
  constructor(data, rules, groups = {}) {
    for (const field in rules) {
      if (!(rules.hasOwnProperty(field))) { continue; }

      const rule = rules[field];

      const criteria = rule.split('|');

      for (const criterion of criteria) {
        if (excludeFeatureFlagPattern.test(criterion)) {
          const [, group] = excludeFeatureFlagPattern.exec(criterion);

          if (groups[group]) {
            delete rules[field];
          }
          break;
        }

        if (featureFlagPattern.test(criterion)) {
          const [, group] = featureFlagPattern.exec(criterion);

          if (!groups[group]) {
            delete rules[field];
          }
          break;
        }

        if (excludeCriteriaForFeatureFlagPattern.test(criterion)) {
          const [, definition] = excludeCriteriaForFeatureFlagPattern.exec(criterion);
          const [group, ...criteriaToIncludeForNonMembers] = definition.split(',');

          if (!groups[group]) {
            rules[field] += `|${criteriaToIncludeForNonMembers.join('|')}`;
          }
          break;
        }
      }
    }

    return new AdValidator.Validator(data, rules, AdValidator.CUSTOM_ERR_MSGS);
  }
}

/**
 * Dangled for test mocking
 * @type {Validator}
 */
AdValidator.Validator = Validator;

AdValidator.CUSTOM_ERR_MSGS = {};



// WEBPACK FOOTER //
// ./src/lib/AdValidator.js